import { useState, useEffect } from 'react'
import { catchError, concatMap, first, NEVER, Observable } from 'rxjs'
import debug from 'libraries/front-end-core/core/debug'
import { refresh } from 'services/ApiService'
import notificationToast from 'helpers/notificationToast'
import ErrorIcon from '@mui/icons-material/Error'
import CheckIcon from '@mui/icons-material/CheckCircle'

interface UseAolUpdateProps {
  currentValue: string
  updateFunction: (value: string) => Observable<any>
  refreshRequest: string
  successMessage: string
}

interface UseAolUpdateReturn {
  selectedAol: string
  changed: boolean
  handleChange: (val: string) => void
  handleSave: () => void
  handleCancel: () => void
}

export const useAolUpdate = ({
  currentValue,
  updateFunction,
  refreshRequest,
  successMessage
}: UseAolUpdateProps): UseAolUpdateReturn => {
  const [selectedAol, setSelectedAol] = useState(currentValue)
  const [changed, setChanged] = useState(false)

  useEffect(() => {
    setSelectedAol(currentValue)
    setChanged(false)
  }, [currentValue])

  const handleChange = (val: string): void => {
    setSelectedAol(val)
    setChanged(val !== currentValue)
  }

  const handleSave = (): void => {
    updateFunction(selectedAol)
      .pipe(
        catchError((e: any) => {
          debug.warn(e)
          notificationToast(
            <ErrorIcon />,
            'error',
            'Please try again.',
            'bg-indian-red white'
          )
          return NEVER
        }),
        concatMap(() => refresh(refreshRequest)),
        first()
      )
      .subscribe(() => {
        notificationToast(
          <CheckIcon />,
          'success',
          successMessage,
          'bg-dark-sea-green white'
        )
        setChanged(false)
      })
  }

  const handleCancel = (): void => {
    setSelectedAol(currentValue)
    setChanged(false)
  }

  return { selectedAol, changed, handleChange, handleSave, handleCancel }
}
